import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import { getArtworks } from '@/lib/api';
import PictureImg from '@/components/PictureImg';
import { getArtworkAspectRatio } from '@/lib/utils';

const FILTERS = [
    { v: 'all', l: 'All works' },
    { v: 'available', l: 'Available' },
    { v: 'reserved', l: 'Reserved' },
];

export default function Artworks() {
    useDocumentMeta({
        title: 'Artworks · Maison Touré',
        description: 'Selected contemporary African artworks available through Maison Touré, Sydney — provenance documented, privately viewed.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const hero = settings?.artworks_page || {};
    const [works, setWorks] = useState([]);
    const [filter, setFilter] = useState('all');
    useEffect(() => { getArtworks().then(setWorks); }, []);

    const filtered = filter === 'all' ? works : works.filter((w) => w.availability.toLowerCase() === filter);

    return (
        <div ref={reveal} data-testid="artworks-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12 pb-40 md:pb-56">
                <PageHero
                    testid="artworks-hero"
                    overline={hero.hero_overline ?? 'The Collection'}
                    title={hero.hero_title ?? 'Artworks.'}
                    intro={hero.hero_intro ?? 'A curated selection of available works. Full provenance documentation, certificate of authenticity, and a condition report accompany every acquisition.'}
                />

                <div className="mt-20 md:mt-24 flex gap-12 rule-bottom pb-6">
                    {FILTERS.map((f) => (
                        <button
                            key={f.v}
                            data-testid={`filter-${f.v}`}
                            onClick={() => setFilter(f.v)}
                            className="overline transition-all duration-500"
                            style={{
                                color: filter === f.v ? 'var(--ink)' : 'var(--ink-3)',
                                letterSpacing: filter === f.v ? '0.42em' : '0.34em',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>

                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-24">
                    {filtered.map((w, i) => (
                        <Link
                            to={`/artworks/${w.slug}`}
                            key={w.slug}
                            className={`reveal group block ${i % 7 === 3 ? 'md:mt-24' : ''} ${i % 5 === 2 ? 'lg:mt-16' : ''}`}
                            data-testid={`artwork-card-${w.slug}`}
                        >
                            <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)', aspectRatio: getArtworkAspectRatio(w) }} onContextMenu={(e) => e.preventDefault()}>
                                <PictureImg src={w.image} alt={w.title} loading="lazy" className="no-save" />
                            </div>
                            <div className="mt-8">
                                <div
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontSize: 16,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {w.artist_name}
                                </div>
                                <h3
                                    className="mt-2"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 'clamp(24px, 2.4vw, 34px)',
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.012em',
                                    }}
                                >
                                    {w.title}, <span style={{ color: 'var(--ink-3)' }}>{w.year}</span>
                                </h3>
                                <div className="mt-5 flex items-baseline justify-between gap-4">
                                    <span
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {w.medium.split(',')[0]}
                                    </span>
                                    <span
                                        style={{
                                            fontFamily: "'Jost', sans-serif",
                                            fontSize: 10,
                                            letterSpacing: '0.34em',
                                            textTransform: 'uppercase',
                                            color: 'var(--ink-2)',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {w.price_on_request ? 'On Request' : `${w.currency} ${w.price?.toLocaleString()}`}
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
